import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import { countOccupants } from 'lib/offer/occupancyUtils'

export interface PropertyAnytimeRatesVisibilityInput {
  /** Marketing traffic is always shown an anytime rate. */
  utmBp: string | undefined
  /** `PropertyOfferMapping.hasPromotions`. */
  mappingHasPromotions: boolean
  // TODO(HP-2326): Remove these fallbacks once the mapping endpoint reliably returns
  // `hasPromotions`.
  hasAlwaysOnPromotions: boolean
  hasBedbankPromotions: boolean
}

/**
 * Whether anytime rates may be surfaced for a property.
 *
 * This used to be implicit: server-side rendering only fetched anytime rates when the answer was
 * yes, so anywhere downstream could treat their presence in state as permission to display them.
 * Search tiles now fetch them too — for every LPC/LPP property on an anytime search, to find the
 * ones running a dynamic Flash campaign — so presence is no longer evidence on its own and each
 * display site has to ask.
 */
export function canSurfacePropertyAnytimeRates({
  utmBp,
  mappingHasPromotions,
  hasAlwaysOnPromotions,
  hasBedbankPromotions,
}: PropertyAnytimeRatesVisibilityInput): boolean {
  return !!utmBp || mappingHasPromotions || hasAlwaysOnPromotions || hasBedbankPromotions
}

/**
 * Offer types whose anytime rates may drive a displayed price.
 *
 * Dynamic Flash campaign promotions only run on LPC (always on hotel) properties for now, so
 * nothing else has an anytime price worth sourcing — LPP and villa tiles keep the pricing search
 * gives them.
 */
export const ANYTIME_RATE_PRICE_SOURCE_OFFER_TYPES: ReadonlyArray<App.OfferType> = [OFFER_TYPE_ALWAYS_ON]

export function canSourcePriceFromPropertyAnytimeRate(offerType: App.OfferType | undefined): boolean {
  return !!offerType && ANYTIME_RATE_PRICE_SOURCE_OFFER_TYPES.includes(offerType)
}

/**
 * The occupancy an anytime rate is quoted for: one room of no more than two adults.
 *
 * Search and the offer page have to agree on this to the letter — the tile's price is only worth
 * showing if the offer page will honour it — so both read it from here.
 */
export function isPropertyAnytimeRateOccupancy(rooms: Array<App.Occupants>): boolean {
  if (rooms.length !== 1) return false

  const { adults, teenagers, children, infants } = countOccupants(rooms)
  return adults <= 2 && teenagers === 0 && children === 0 && infants === 0
}

export interface PropertyAnytimeRateApplicabilityInput {
  /** Length of stay the anytime rate prices, in nights. */
  rateDuration: number | undefined
  /** Length of stay the price is presented as covering, in nights. */
  displayedDuration: number | undefined
  hasStandardOccupancy: boolean
}

/**
 * Whether an anytime rate prices the stay the customer is actually looking at.
 *
 * Anytime rates are quoted for a fixed length of stay at standard occupancy, so showing one
 * against any other stay would quote a price we can't honour — and a search tile labels its price
 * with a night count of its own, which the rate has to agree with. We'd much rather the customer
 * set dates and get a real price.
 */
export function canApplyPropertyAnytimeRate({
  rateDuration,
  displayedDuration,
  hasStandardOccupancy,
}: PropertyAnytimeRateApplicabilityInput): boolean {
  return hasStandardOccupancy && !!rateDuration && rateDuration === displayedDuration
}
